import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
    Star, MapPin, Phone, Mail, Globe, Briefcase, Languages, Award,
    Loader2, ShieldCheck, MessageSquare, Calendar, Video, Building2,
} from "lucide-react";
import { trackEvent } from "../lib/tracker";

// Tier rendered as a NEUTRAL badge — explicitly NOT stars, so it can never be
// confused with the gold-star client rating shown alongside.
const TIER_LABEL = { basic: "Basic", featured: "Pro", top_spot: "Elite" };
const TIER_BADGE_STYLE = {
    basic: "bg-muted text-foreground/80",
    featured: "bg-primary/10 text-primary border border-primary/30",
    top_spot: "bg-amber-200/40 text-amber-900 border border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-200",
};

function Stars({ value, size = "h-4 w-4" }) {
    const full = Math.round(value || 0);
    return (
        <span className="inline-flex items-center" aria-label={`${value} out of 5 stars`} data-testid="stars-display">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`${size} ${i <= full ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
                />
            ))}
        </span>
    );
}

function ConsultationIcons({ options }) {
    const map = { phone: <Phone className="h-3.5 w-3.5" />, video: <Video className="h-3.5 w-3.5" />, "in-person": <Building2 className="h-3.5 w-3.5" /> };
    return (
        <div className="flex flex-wrap gap-2 mt-2">
            {(options || []).map((o) => (
                <span key={o} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-accent/60 border border-border px-2.5 py-1" data-testid={`consult-${o}`}>
                    {map[o] || <Calendar className="h-3.5 w-3.5" />}
                    {o.replace(/-/g, " ")}
                </span>
            ))}
        </div>
    );
}

function ReviewForm({ attorneyId, onSubmitted }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [hover, setHover] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!name.trim() || !email.trim() || !comment.trim() || comment.trim().length < 10) {
            setErr("Please add your name, email, and a comment of at least 10 characters.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/attorneys/${attorneyId}/reviews`, {
                reviewer_name: name.trim(), reviewer_email: email.trim(), rating, comment: comment.trim(),
            });
            setDone(true);
            onSubmitted?.();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Couldn't submit your review. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="ll-card border-primary/40 bg-primary/5" data-testid="review-submitted">
                <h3 className="font-serif text-lg font-semibold text-primary mb-1">Thank you</h3>
                <p className="text-sm">Your review has been submitted for moderation. It will appear here once approved by our team.</p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="ll-card" data-testid="review-form">
            <h3 className="font-serif text-lg font-semibold mb-1">Leave a client review</h3>
            <p className="text-xs text-muted-foreground mb-4">All reviews are held for moderation. Please share honest, respectful, and specific feedback. Do not include private case details or external links.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <input className="ll-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="review-name" />
                <input className="ll-input" placeholder="Email (not shown publicly)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="review-email" />
            </div>

            <div className="mb-3">
                <div className="ll-label mb-1.5">Your rating</div>
                <div className="flex items-center gap-1" data-testid="review-stars-input">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <button
                            key={i}
                            type="button"
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(0)}
                            onClick={() => setRating(i)}
                            aria-label={`${i} star${i > 1 ? "s" : ""}`}
                            data-testid={`review-star-${i}`}
                            className="p-1 rounded">
                            <Star className={`h-6 w-6 ${(hover || rating) >= i ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
                        </button>
                    ))}
                </div>
            </div>

            <textarea
                className="ll-input resize-none"
                rows={4}
                placeholder="Share your experience working with this attorney. What stood out? How did they communicate?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid="review-comment"
            />

            {err && <div className="mt-3 text-sm text-destructive" data-testid="review-form-error">{err}</div>}

            <button
                type="submit"
                disabled={submitting}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                data-testid="review-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                Submit review
            </button>
        </form>
    );
}

export default function AttorneyPublicProfilePage() {
    const { attorneyId } = useParams();
    const [profile, setProfile] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const loadReviews = async () => {
        try {
            const { data } = await api.get(`/attorneys/${attorneyId}/reviews?limit=20`);
            setReviews(data || []);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/attorneys/${attorneyId}`);
                if (alive) setProfile(data);
                // Track that an attorney profile was viewed — this powers the
                // attorneys' "your profile got X views this week" insight later.
                trackEvent("attorney_profile_view", data?.name || attorneyId, {
                    attorney_id: attorneyId,
                });
                await loadReviews();
            } catch (e) {
                if (e?.response?.status === 404) setNotFound(true);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [attorneyId]); // eslint-disable-line

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-16 flex items-center justify-center" data-testid="attorney-profile-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }
    if (notFound || !profile) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="attorney-profile-not-found">
                <h1 className="font-serif text-2xl font-semibold mb-2">Attorney not found</h1>
                <p className="text-sm text-muted-foreground">This profile may have been removed or the link is incorrect.</p>
                <Link to="/" className="inline-block mt-6 text-primary hover:underline">← Back home</Link>
            </div>
        );
    }

    const tierLabel = TIER_LABEL[profile.profile_tier];
    const tierStyle = TIER_BADGE_STYLE[profile.profile_tier];

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="attorney-public-profile">
            {/* HERO */}
            <div className="ll-card mb-8" data-testid="attorney-profile-header">
                <div className="flex flex-col sm:flex-row gap-6">
                    {profile.photo_url ? (
                        <img
                            src={profile.photo_url}
                            alt={profile.name}
                            className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover flex-shrink-0 border-2 border-border"
                            data-testid="attorney-photo"
                        />
                    ) : (
                        <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-muted flex items-center justify-center flex-shrink-0 border-2 border-border text-4xl font-serif text-muted-foreground" data-testid="attorney-photo-placeholder">
                            {profile.name?.[0] || "?"}
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start gap-3 mb-1">
                            <h1 className="font-serif text-3xl font-bold tracking-tight" data-testid="attorney-name">{profile.name}</h1>
                            {tierLabel && (
                                <span
                                    className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${tierStyle}`}
                                    title="Profile tier — set by the attorney's plan, not a client rating"
                                    data-testid="attorney-tier-badge">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    {tierLabel} profile tier
                                </span>
                            )}
                        </div>
                        {profile.firm_name && (
                            <div className="text-base text-muted-foreground mb-2" data-testid="attorney-firm">{profile.firm_name}</div>
                        )}

                        {/* Client rating — VISUALLY DISTINCT from the tier badge. */}
                        {profile.rating?.count > 0 ? (
                            <div className="flex items-center gap-2 mb-3" data-testid="attorney-rating">
                                <Stars value={profile.rating.average} />
                                <span className="text-sm font-medium">{profile.rating.average?.toFixed(1)}</span>
                                <span className="text-sm text-muted-foreground">· {profile.rating.count} client review{profile.rating.count === 1 ? "" : "s"}</span>
                            </div>
                        ) : (
                            <div className="text-xs text-muted-foreground mb-3" data-testid="attorney-no-rating">No client reviews yet</div>
                        )}

                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                            {(profile.service_area_city || profile.service_area_state) && (
                                <span className="inline-flex items-center gap-1" data-testid="attorney-service-area">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {[profile.service_area_city, profile.service_area_state].filter(Boolean).join(", ")}
                                </span>
                            )}
                            {profile.years_experience != null && (
                                <span className="inline-flex items-center gap-1" data-testid="attorney-years">
                                    <Briefcase className="h-3.5 w-3.5" />
                                    {profile.years_experience} years experience
                                </span>
                            )}
                            {(profile.languages || []).length > 0 && (
                                <span className="inline-flex items-center gap-1" data-testid="attorney-languages">
                                    <Languages className="h-3.5 w-3.5" />
                                    {profile.languages.join(", ")}
                                </span>
                            )}
                        </div>

                        <ConsultationIcons options={profile.consultation_options} />
                    </div>
                </div>

                {/* Contact buttons */}
                <div className="mt-6 flex flex-wrap gap-2 pt-6 border-t border-border">
                    {profile.phone && (
                        <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="attorney-call-btn">
                            <Phone className="h-4 w-4" /> Call
                        </a>
                    )}
                    {profile.email_public && (
                        <a href={`mailto:${profile.email_public}`} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-accent" data-testid="attorney-email-btn">
                            <Mail className="h-4 w-4" /> Email
                        </a>
                    )}
                    {profile.website && (
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-accent" data-testid="attorney-website-btn">
                            <Globe className="h-4 w-4" /> Website
                        </a>
                    )}
                </div>
            </div>

            {/* PRACTICE AREAS + BIO + DETAILS */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 space-y-6">
                    {profile.bio && (
                        <section className="ll-card" data-testid="attorney-bio-section">
                            <h2 className="font-serif text-xl font-semibold mb-3">About</h2>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
                        </section>
                    )}
                    {profile.notable_results && (
                        <section className="ll-card" data-testid="attorney-results-section">
                            <h2 className="font-serif text-xl font-semibold mb-3 flex items-center gap-2"><Award className="h-5 w-5 text-primary" /> Notable results</h2>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{profile.notable_results}</p>
                            <p className="mt-3 text-[11px] text-muted-foreground">Past results do not guarantee future outcomes.</p>
                        </section>
                    )}
                </div>
                <aside className="space-y-4">
                    {(profile.practice_areas || []).length > 0 && (
                        <section className="ll-card" data-testid="attorney-practice-areas">
                            <h3 className="ll-label mb-2">Practice areas</h3>
                            <ul className="space-y-1 text-sm">
                                {profile.practice_areas.map((c) => (
                                    <li key={c.id} className="inline-block mr-1.5 mb-1.5 rounded-full bg-accent px-3 py-1 text-xs">{c.name}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                    {(profile.jurisdictions || []).length > 0 && (
                        <section className="ll-card" data-testid="attorney-jurisdictions">
                            <h3 className="ll-label mb-2">Licensed jurisdictions</h3>
                            <ul className="text-sm space-y-1">
                                {profile.jurisdictions.map((j) => <li key={j}>· {j}</li>)}
                            </ul>
                            {profile.bar_number && (
                                <div className="mt-2 text-xs text-muted-foreground">Bar #: {profile.bar_number}</div>
                            )}
                        </section>
                    )}
                </aside>
            </div>

            {/* CLIENT REVIEWS */}
            <section className="mb-8" data-testid="attorney-reviews-section">
                <h2 className="font-serif text-2xl font-semibold mb-1">Client reviews</h2>
                <p className="text-xs text-muted-foreground mb-4">Reviews are submitted by clients and held for moderation before going live.</p>
                {reviews.length === 0 ? (
                    <div className="ll-card text-sm text-muted-foreground" data-testid="no-reviews">No approved reviews yet — be the first to share your experience below.</div>
                ) : (
                    <div className="space-y-3">
                        {reviews.map((r) => (
                            <article key={r.id} className="ll-card" data-testid={`review-${r.id}`}>
                                <header className="flex items-center justify-between gap-2 mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{r.reviewer_display_name}</span>
                                        {r.is_verified_contact && (
                                            <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-primary/10 text-primary border border-primary/30 px-2 py-0.5" data-testid="verified-contact-badge">
                                                <ShieldCheck className="h-3 w-3" /> Verified contact
                                            </span>
                                        )}
                                    </div>
                                    <Stars value={r.rating} size="h-3.5 w-3.5" />
                                </header>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.comment}</p>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <ReviewForm attorneyId={attorneyId} onSubmitted={loadReviews} />

            <p className="text-center text-xs text-muted-foreground mt-8">
                Information on this page is provided by the attorney and/or their clients. LocalLawFinder is a directory and does not endorse or recommend any specific attorney.
            </p>
        </div>
    );
}
